import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { PageHero, PageShell } from "@/components/PageShell";
import { formatRupiah } from "@/lib/currency";
import { getKategoriIcon, getKategoriTheme } from "@/lib/categoryPresentation";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDONESIAN_MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

const getCurrentYearMonth = () => {
  const now = new Date();
  // Default: next month
  let month = now.getMonth() + 2; // getMonth() is 0-based, +1 for current, +1 for next
  let year = now.getFullYear();
  if (month > 12) { month = 1; year += 1; }
  return { month, year };
};

const formatRupiahCompact = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  return formatRupiah(Number(val));
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const MissingPriceBadge = () => (
  <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
    No data bulan lalu
  </span>
);

const ManualPriceBadge = () => (
  <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">
    Harga manual
  </span>
);

const SummaryCard = ({ label, value, sub, tone = "emerald" }) => {
  const toneClass = {
    emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white text-emerald-900",
    amber:   "border-amber-200 bg-gradient-to-br from-amber-50 to-white text-amber-900",
    sky:     "border-sky-200 bg-gradient-to-br from-sky-50 to-white text-sky-900",
  }[tone] || "border-slate-200 bg-white text-slate-900";

  return (
    <div className={cn("rounded-2xl border p-5 shadow-sm", toneClass)}>
      <p className="mb-1 text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-xl font-manrope font-bold">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────

const RabPage = () => {
  const { month: defaultMonth, year: defaultYear } = getCurrentYearMonth();

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear, setSelectedYear]   = useState(defaultYear);
  const [items, setItems]                 = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [exporting, setExporting]         = useState("");
  const [currentRabId, setCurrentRabId]   = useState(null);
  const [currentTitle, setCurrentTitle]   = useState("");
  const [notes, setNotes]                 = useState("");
  const [status, setStatus]               = useState("draft");
  const [collapsedKat, setCollapsedKat]   = useState({});
  const [savedRabList, setSavedRabList]   = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchTerm, setSearchTerm]       = useState("");

  // Year options: 3 years back, 3 forward
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);

  // ── Derived: group items by kategori ──────────────────────────────────
  const groupedByKategori = useMemo(() => {
    const map = {};
    const filtered = searchTerm
      ? items.filter((it) => it.nama_barang.toLowerCase().includes(searchTerm.toLowerCase()))
      : items;
    for (const item of filtered) {
      const kid = item.kategori_id || "lainnya";
      const kname = item.nama_kategori || "Lainnya";
      if (!map[kid]) map[kid] = { kategori_id: kid, nama: kname, items: [] };
      map[kid].items.push(item);
    }
    return Object.values(map).sort((a, b) => a.nama.localeCompare(b.nama));
  }, [items, searchTerm]);

  // ── Derived: summary ──────────────────────────────────────────────────
  const summary = useMemo(() => {
    let totalDefined = 0;
    let totalMissing = 0;
    const byKat = {};
    for (const item of items) {
      const lt = item.line_total;
      const kid = item.kategori_id || "lainnya";
      const kname = item.nama_kategori || "Lainnya";
      if (!byKat[kid]) byKat[kid] = { kategori_id: kid, kategori_nama: kname, total_cost: 0 };
      if (lt !== null && lt !== undefined) {
        totalDefined += lt;
        byKat[kid].total_cost += lt;
      } else {
        totalMissing += 1;
      }
    }
    return {
      total_items: items.length,
      total_cost_defined: totalDefined,
      total_cost_missing: totalMissing,
      by_kategori: Object.values(byKat),
    };
  }, [items]);

  // ── Fetch saved RAB list ───────────────────────────────────────────────
  const fetchRabList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rab`);
      setSavedRabList(res.data || []);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => { fetchRabList(); }, [fetchRabList]);

  // ── Generate skeleton ──────────────────────────────────────────────────
  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/rab/generate?month=${selectedMonth}&year=${selectedYear}`
      );
      const raw = res.data.items || [];
      setItems(enrichItems(raw));
      setCurrentRabId(null);
      setCurrentTitle(`RAB ${INDONESIAN_MONTHS[selectedMonth - 1]} ${selectedYear}`);
      setNotes("");
      setStatus("draft");
      setSearchTerm("");
      toast.success(`Skeleton RAB ${INDONESIAN_MONTHS[selectedMonth - 1]} ${selectedYear} berhasil di-generate`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal generate RAB");
    } finally {
      setLoading(false);
    }
  };

  // ── Update item ────────────────────────────────────────────────────────
  const updateItem = useCallback((barang_id, field, rawValue) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.barang_id !== barang_id) return item;

        let updated = { ...item };

        if (field === "price_used") {
          updated.price_used = rawValue === "" ? null : Number(rawValue);
        } else if (field === "qty_input") {
          updated.qty_input = rawValue === "" ? 0 : Number(rawValue);
        } else if (field === "satuan_input") {
          updated.satuan_input = rawValue;
        } else {
          updated[field] = rawValue === "" ? null : rawValue;
        }

        // Recompute qty_planned from qty_input * konversi faktor
        const konversi = updated.konversi_satuan || {};
        const satuan_input = (updated.satuan_input || updated.satuan || "").toLowerCase().trim();
        const faktor = konversi[satuan_input] ?? 1.0;
        const qty_input = updated.qty_input ?? 0;
        updated.qty_planned = qty_input * faktor;

        // Recompute line_total
        const p = updated.price_used;
        const q = updated.qty_planned;
        updated.line_total =
          p !== null && p !== undefined && q !== null && q !== undefined && q > 0 && p > 0
            ? parseFloat((p * q).toFixed(2))
            : null;

        return updated;
      })
    );
  }, []);

  // ── Save RAB ───────────────────────────────────────────────────────────
  const handleSave = async (newStatus = status) => {
    if (items.length === 0) {
      toast.error("Generate RAB terlebih dahulu sebelum menyimpan");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        year: selectedYear,
        month: selectedMonth,
        title: currentTitle || `RAB ${INDONESIAN_MONTHS[selectedMonth - 1]} ${selectedYear}`,
        status: newStatus,
        items,
        notes,
      };
      let res;
      if (currentRabId) {
        res = await axios.put(`${API}/rab/${currentRabId}`, payload);
      } else {
        res = await axios.post(`${API}/rab`, payload);
        setCurrentRabId(res.data.rab_id);
      }
      setStatus(res.data.status);
      toast.success(
        newStatus === "published" ? "RAB berhasil dipublikasikan" : "RAB berhasil disimpan sebagai draft"
      );
      fetchRabList();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan RAB");
    } finally {
      setSaving(false);
    }
  };

  // ── Enrich items: pastikan available_units & konversi_satuan ada ────────
  const enrichItems = useCallback((rawItems) => {
    return rawItems.map((it) => {
      const satDasar = (it.satuan || "").toLowerCase().trim();
      // Bangun konversi: ambil dari item atau buat default
      const konversi = Object.fromEntries(
        Object.entries(it.konversi_satuan || {}).map(([k, v]) => [k.toLowerCase().trim(), Number(v)])
      );
      if (satDasar && !(satDasar in konversi)) {
        konversi[satDasar] = 1.0;
      }
      // Bangun available_units: satuan dasar pertama, sisanya alpha
      const others = Object.keys(konversi).filter((u) => u && u !== satDasar).sort();
      const available_units = satDasar ? [satDasar, ...others] : others;
      // satuan_input default ke satuan dasar jika belum diset atau tidak valid
      const satuan_input =
        it.satuan_input && available_units.includes(it.satuan_input)
          ? it.satuan_input
          : satDasar;
      // qty_input: fallback dari qty_planned jika qty_input belum ada
      const qty_input = it.qty_input ?? it.qty_planned ?? 0;
      return {
        ...it,
        konversi_satuan: konversi,
        available_units,
        satuan_input,
        qty_input,
      };
    });
  }, []);

  // ── Load saved RAB ─────────────────────────────────────────────────────
  const handleLoadRab = async (rab_id) => {
    try {
      const res = await axios.get(`${API}/rab/${rab_id}`);
      const doc = res.data;
      setCurrentRabId(doc.rab_id);
      setSelectedMonth(doc.month);
      setSelectedYear(doc.year);
      setCurrentTitle(doc.title);
      setNotes(doc.notes || "");
      setStatus(doc.status);
      setItems(enrichItems(doc.items || []));
      setSearchTerm("");
      setLoadDialogOpen(false);
      toast.success(`RAB "${doc.title}" berhasil dimuat`);
    } catch (err) {
      toast.error("Gagal memuat RAB");
    }
  };

  // ── Delete RAB ─────────────────────────────────────────────────────────
  const handleDeleteRab = async (rab_id) => {
    try {
      await axios.delete(`${API}/rab/${rab_id}`);
      setSavedRabList((prev) => prev.filter((r) => r.rab_id !== rab_id));
      if (currentRabId === rab_id) {
        setCurrentRabId(null);
        setItems([]);
        setCurrentTitle("");
      }
      setDeleteConfirmId(null);
      toast.success("RAB berhasil dihapus");
    } catch (err) {
      toast.error("Gagal menghapus RAB");
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────
  const handleExport = async (format) => {
    if (!currentRabId) {
      toast.error("Simpan RAB terlebih dahulu sebelum export");
      return;
    }
    setExporting(format);
    try {
      const res = await axios.get(`${API}/rab/${currentRabId}/export?format=${format}`, {
        responseType: "blob",
      });
      const mime =
        format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
      const ext = format;
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RAB-${INDONESIAN_MONTHS[selectedMonth - 1]}-${selectedYear}.${ext}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 1000);
      toast.success(`Export ${format.toUpperCase()} berhasil`);
    } catch {
      toast.error(`Gagal export ${format.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  // ── Toggle kategori collapse ───────────────────────────────────────────
  const toggleKat = (kid) =>
    setCollapsedKat((prev) => ({ ...prev, [kid]: !prev[kid] }));

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <PageShell>
      <PageHero
        title="Rencana Anggaran Biaya (RAB)"
        description="Rencanakan pemakaian bahan dapur per bulan berdasarkan harga rata-rata bulan sebelumnya."
      />

      {/* ── Controls bar ── */}
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white/80 p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          {/* Month */}
          <div className="min-w-[140px]">
            <Label>Bulan</Label>
            <Select
              value={String(selectedMonth)}
              onValueChange={(v) => setSelectedMonth(Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDONESIAN_MONTHS.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year */}
          <div className="min-w-[110px]">
            <Label>Tahun</Label>
            <Select
              value={String(selectedYear)}
              onValueChange={(v) => setSelectedYear(Number(v))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="flex-1 min-w-[180px]">
            <Label>Judul RAB</Label>
            <Input
              value={currentTitle}
              onChange={(e) => setCurrentTitle(e.target.value)}
              placeholder={`RAB ${INDONESIAN_MONTHS[selectedMonth - 1]} ${selectedYear}`}
            />
          </div>

          {/* Generate */}
          <Button
            type="button"
            onClick={handleGenerate}
            disabled={loading}
            className="shrink-0"
          >
            <Plus size={16} className="mr-2" />
            {loading ? "Memuat..." : "Generate RAB"}
          </Button>

          {/* Load existing */}
          <Button
            type="button"
            variant="outline"
            onClick={() => { fetchRabList(); setLoadDialogOpen(true); }}
            className="shrink-0"
          >
            <FileText size={16} className="mr-2" />
            Buka RAB Tersimpan
          </Button>
        </div>

        {items.length > 0 && (
          <div className="mt-3">
            <Label>Catatan</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Catatan opsional untuk RAB ini"
            />
          </div>
        )}
      </div>

      {items.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 py-20 text-center">
          <FileText size={48} className="mb-4 text-slate-300" />
          <p className="font-manrope font-semibold text-slate-500">Belum ada data RAB</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Pilih bulan & tahun, lalu klik <span className="font-medium">Generate RAB</span> untuk memulai.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <>
          {/* ── Summary cards ── */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SummaryCard
              label="Total Estimasi Biaya"
              value={formatRupiahCompact(summary.total_cost_defined)}
              sub={`${summary.total_items} bahan`}
              tone="emerald"
            />
            <SummaryCard
              label="Item Tanpa Harga"
              value={`${summary.total_cost_missing} item`}
              sub="Harga belum diisi atau tidak ada data"
              tone="amber"
            />
            <SummaryCard
              label="Item Terdefinisi"
              value={`${summary.total_items - summary.total_cost_missing} item`}
              sub="Sudah ada harga & qty"
              tone="sky"
            />
          </div>

          {/* ── Per-kategori summary bar ── */}
          {summary.by_kategori.length > 0 && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
              <p className="mb-3 font-manrope font-bold text-slate-800 text-sm">Ringkasan per Kategori</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="py-2 text-left font-semibold text-slate-600">Kategori</th>
                      <th className="py-2 text-right font-semibold text-slate-600">Total Estimasi</th>
                      <th className="py-2 text-right font-semibold text-slate-600">Jumlah Item</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_kategori
                      .sort((a, b) => b.total_cost - a.total_cost)
                      .map((kat) => (
                        <tr key={kat.kategori_id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-2 font-medium">{kat.kategori_nama}</td>
                          <td className="py-2 text-right text-emerald-700 font-medium">
                            {formatRupiahCompact(kat.total_cost)}
                          </td>
                          <td className="py-2 text-right text-slate-500">
                            {items.filter((it) => it.kategori_id === kat.kategori_id).length}
                          </td>
                        </tr>
                      ))}
                    <tr className="border-t-2 font-bold">
                      <td className="py-2">Total Keseluruhan</td>
                      <td className="py-2 text-right text-emerald-800">
                        {formatRupiahCompact(summary.total_cost_defined)}
                      </td>
                      <td className="py-2 text-right">{summary.total_items}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Search ── */}
          <div className="mb-4">
            <Input
              placeholder="Cari nama barang..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {/* ── Item table per kategori ── */}
          <div className="space-y-4 mb-6">
            {groupedByKategori.map((kat) => {
              const KatIcon = getKategoriIcon(kat.nama);
              const katTheme = getKategoriTheme(kat.nama);
              const isCollapsed = collapsedKat[kat.kategori_id];
              const katTotal = kat.items.reduce((acc, it) => acc + (it.line_total || 0), 0);

              return (
                <div
                  key={kat.kategori_id}
                  className="rounded-2xl border border-slate-200 bg-white/90 shadow-sm overflow-hidden"
                >
                  {/* Kategori header */}
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between px-5 py-3 transition-colors hover:bg-muted/30",
                      katTheme?.bg || "bg-slate-50"
                    )}
                    onClick={() => toggleKat(kat.kategori_id)}
                  >
                    <div className="flex items-center gap-3">
                      {isCollapsed ? (
                        <ChevronRight size={18} className={katTheme?.iconText || "text-slate-500"} />
                      ) : (
                        <ChevronDown size={18} className={katTheme?.iconText || "text-slate-500"} />
                      )}
                      {KatIcon && <KatIcon size={18} className={katTheme?.iconText || "text-slate-500"} />}
                      <span className={cn("font-manrope font-bold text-base", katTheme?.titleText || "text-slate-800")}>
                        {kat.nama}
                      </span>
                      <span className="text-xs text-muted-foreground">({kat.items.length} item)</span>
                    </div>
                    <span className="font-manrope font-semibold text-sm text-emerald-700">
                      {katTotal > 0 ? formatRupiahCompact(katTotal) : "-"}
                    </span>
                  </button>

                  {!isCollapsed && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="px-3 py-2 text-center w-10">No</th>
                            <th className="px-3 py-2 text-left">Nama Barang</th>
                            <th className="px-3 py-2 text-center w-28">Qty Rencana</th>
                            <th className="px-3 py-2 text-center w-28">Satuan</th>
                            <th className="px-3 py-2 text-right w-36">Harga Ref (Avg)</th>
                            <th className="px-3 py-2 text-right w-36">Harga Digunakan</th>
                            <th className="px-3 py-2 text-right w-36">Total Biaya</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kat.items.map((item, idx) => {
                            const isManual =
                              item.price_used !== null &&
                              item.price_used !== undefined &&
                              item.price_used !== item.avg_price_prev;
                            return (
                              <tr key={item.barang_id} className="border-t hover:bg-muted/20">
                                <td className="px-3 py-2 text-center text-muted-foreground">{idx + 1}</td>
                                <td className="px-3 py-2">
                                  <div className="flex flex-col gap-1">
                                    <span className="font-medium">{item.nama_barang}</span>
                                    {item.avg_price_prev === null && <MissingPriceBadge />}
                                    {isManual && <ManualPriceBadge />}
                                  </div>
                                </td>
                                 {/* Qty Rencana (input) */}
                                 <td className="px-3 py-2">
                                   <Input
                                     type="number"
                                     min="0"
                                     step="any"
                                     value={item.qty_input ?? ""}
                                     onChange={(e) =>
                                       updateItem(item.barang_id, "qty_input", e.target.value)
                                     }
                                     placeholder="0"
                                     className="h-8 text-center text-sm"
                                   />
                                 </td>
                                 {/* Satuan (select if multiple, text otherwise) */}
                                 <td className="px-3 py-2 text-center text-muted-foreground">
                                   {item.available_units && item.available_units.length > 1 ? (
                                     <Select
                                       value={item.satuan_input || item.satuan}
                                       onValueChange={(v) => updateItem(item.barang_id, "satuan_input", v)}
                                     >
                                       <SelectTrigger className="h-8 w-28 text-xs">
                                         <SelectValue />
                                       </SelectTrigger>
                                       <SelectContent>
                                         {item.available_units.map((u) => {
                                           const satDasar = (item.satuan || "").toLowerCase().trim();
                                           const faktor = (item.konversi_satuan || {})[u];
                                           const label =
                                             u === satDasar || faktor === undefined || faktor === 1
                                               ? u
                                               : `${u} (${faktor} ${satDasar})`;
                                           return (
                                             <SelectItem key={u} value={u}>{label}</SelectItem>
                                           );
                                         })}
                                       </SelectContent>
                                     </Select>
                                   ) : (
                                     item.satuan_input || item.satuan
                                   )}
                                 </td>
                                 {/* Harga Ref */}
                                 <td className="px-3 py-2 text-right text-slate-500 text-xs">
                                   {item.avg_price_prev !== null && item.avg_price_prev !== undefined
                                     ? formatRupiahCompact(item.avg_price_prev)
                                     : <span className="text-amber-500">-</span>}
                                 </td>
                                 {/* Harga Digunakan (input) */}
                                 <td className="px-3 py-2">
                                   <Input
                                     type="number"
                                     min="0"
                                     step="any"
                                     value={item.price_used ?? ""}
                                     onChange={(e) =>
                                       updateItem(item.barang_id, "price_used", e.target.value)
                                     }
                                     placeholder="0"
                                     className="h-8 text-right text-sm"
                                   />
                                 </td>
                                <td className="px-3 py-2 text-right font-medium">
                                  {item.line_total !== null && item.line_total !== undefined
                                    ? <span className="text-emerald-700">{formatRupiahCompact(item.line_total)}</span>
                                    : <span className="text-slate-400">-</span>}
                                </td>
                              </tr>
                            );
                          })}

                          {/* Subtotal row per kategori */}
                          <tr className="border-t-2 bg-slate-50/80 font-semibold">
                            <td className="px-3 py-2" colSpan={6} style={{ textAlign: "right" }}>
                              Subtotal {kat.nama}:
                            </td>
                            <td className="px-3 py-2 text-right text-emerald-800">
                              {katTotal > 0 ? formatRupiahCompact(katTotal) : "-"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Action buttons ── */}
          <div className="flex flex-wrap gap-3 justify-between items-center rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => handleSave("draft")}
                disabled={saving}
                variant="outline"
              >
                <Save size={16} className="mr-2" />
                {saving ? "Menyimpan..." : "Simpan Draft"}
              </Button>
              <Button
                type="button"
                onClick={() => handleSave("published")}
                disabled={saving}
              >
                <Save size={16} className="mr-2" />
                {saving ? "Menyimpan..." : "Publikasikan"}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleExport("xlsx")}
                disabled={!currentRabId || exporting === "xlsx"}
              >
                <Download size={16} className="mr-2" />
                {exporting === "xlsx" ? "Mengekspor..." : "Export Excel"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleExport("pdf")}
                disabled={!currentRabId || exporting === "pdf"}
              >
                <FileText size={16} className="mr-2" />
                {exporting === "pdf" ? "Mengekspor..." : "Export PDF"}
              </Button>
            </div>
          </div>

          {!currentRabId && (
            <p className="mt-2 text-xs text-muted-foreground text-right">
              Simpan RAB terlebih dahulu untuk mengaktifkan export.
            </p>
          )}
        </>
      )}

      {/* ── Load RAB Dialog ── */}
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RAB Tersimpan</DialogTitle>
          </DialogHeader>
          {savedRabList.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Belum ada RAB tersimpan.</p>
          ) : (
            <div className="space-y-2">
              {savedRabList.map((rab) => (
                <div
                  key={rab.rab_id}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3"
                >
                  <div>
                    <p className="font-manrope font-semibold text-sm">{rab.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {INDONESIAN_MONTHS[rab.month - 1]} {rab.year} ·{" "}
                      <span
                        className={cn(
                          "font-medium",
                          rab.status === "published" ? "text-emerald-600" : "text-amber-600"
                        )}
                      >
                        {rab.status === "published" ? "Published" : "Draft"}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleLoadRab(rab.rab_id)}
                    >
                      Buka
                    </Button>
                    {deleteConfirmId === rab.rab_id ? (
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteRab(rab.rab_id)}
                        >
                          Hapus
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteConfirmId(null)}
                        >
                          Batal
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirmId(rab.rab_id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default RabPage;
